'use client';
import { useTimePilotT } from "@/i18n/useTimePilotT";

import { useEffect, useRef, useState } from 'react';
import { FunnelIcon, FunnelSimpleIcon, XIcon } from '@phosphor-icons/react';
import type { ResolvedColumn } from '@/shared/mobile-service/lib/columns';
import {
  emptyFilter,
  isFilterActive,
  type ColumnFilterValue,
  type NumberFilterOp,
  type StringFilterOp,
} from '@/shared/mobile-service/lib/where';

interface ColumnFilterProps {
  col: ResolvedColumn;
  value: ColumnFilterValue | undefined;
  onChange: (next: ColumnFilterValue | undefined) => void;
}

/**
 * Per-column filter popover. The control set adapts to the column's filter kind
 * (string / number / datetime / boolean / enum) and emits a
 * {@link ColumnFilterValue}; the parent compiles active filters into a
 * Prisma-style ``where`` object.
 */
export function ColumnFilter({ col, value, onChange }: ColumnFilterProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = isFilterActive(value);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!col.filter) return null;

  const current = value ?? emptyFilter(col.filter);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`rounded p-0.5 transition-colors ${
          active ? 'text-brand-blue-light' : 'text-white/40 hover:text-white/65'
        }`}
        aria-label={`Filter ${col.label}`}
      >
        {active ? <FunnelIcon size={14} weight="fill" /> : <FunnelSimpleIcon size={14} />}
      </button>
      {open && (
        <div
          className="absolute right-0 z-20 mt-1 w-60 rounded-md border border-white/10 bg-[#0f1d32] p-3 shadow-lg ring-1 ring-white/10"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/65">
              {col.label}
            </span>
            {active && (
              <button
                onClick={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
                className="flex items-center gap-1 text-xs text-white/50 hover:text-[#FF6B5E]"
              >
                <XIcon size={12} /> Clear
              </button>
            )}
          </div>
          <FilterControl col={col} value={current} onChange={onChange} />
        </div>
      )}
    </div>
  );
}

const STRING_OPS: { value: StringFilterOp; labelKey: string }[] = [
  { value: 'contains', labelKey: "viewers.contains" },
  { value: 'equals', labelKey: "viewers.equals" },
  { value: 'startsWith', labelKey: "viewers.startsWith" },
  { value: 'endsWith', labelKey: "viewers.endsWith" },
];

const NUMBER_OPS: { value: NumberFilterOp; label: string }[] = [
  { value: 'equals', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
];

const inputCls =
  'w-full rounded border border-white/20 bg-white/5 px-2 py-1 text-sm text-white placeholder:text-white/40 focus:border-brand-blue-light focus:outline-none focus:ring-1 focus:ring-brand-blue-light';

function FilterControl({ col, value, onChange }: Required<Omit<ColumnFilterProps, 'value'>> & {
  value: ColumnFilterValue;
}) {
  const t = useTimePilotT();
  if (value.kind === 'string') {
    return (
      <div className="flex flex-col gap-2">
        <select
          className={inputCls}
          value={value.op}
          onChange={(e) => onChange({ ...value, op: e.target.value as StringFilterOp })}
        >
          {STRING_OPS.map((o) => (
            <option key={o.value} value={o.value}>
              {t(o.labelKey)}
            </option>
          ))}
        </select>
        <input
          autoFocus
          className={inputCls}
          value={value.value}
          placeholder={t("viewers.value")}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
      </div>
    );
  }

  if (value.kind === 'number') {
    return (
      <div className="flex gap-2">
        <select
          className="rounded border border-white/20 bg-white/5 px-1 text-sm text-white focus:border-brand-blue-light focus:outline-none"
          value={value.op}
          onChange={(e) => onChange({ ...value, op: e.target.value as NumberFilterOp })}
        >
          {NUMBER_OPS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          autoFocus
          type="number"
          className={inputCls}
          value={value.value}
          placeholder={t("viewers.value")}
          onChange={(e) => onChange({ ...value, value: e.target.value })}
        />
      </div>
    );
  }

  if (value.kind === 'boolean') {
    return (
      <select
        className={inputCls}
        value={value.value ? 'true' : 'false'}
        onChange={(e) => onChange({ kind: 'boolean', value: e.target.value === 'true' })}
      >
        <option value="true">{t("viewers.true")}</option>
        <option value="false">{t("viewers.false")}</option>
      </select>
    );
  }

  if (value.kind === 'enum') {
    const options = col.enumValues ?? [];
    const selected = new Set(value.values);
    return (
      <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
        {options.length === 0 && (
          <span className="text-xs text-white/50">{t("viewers.noEnumValuesConfigured")}</span>
        )}
        {options.map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={selected.has(opt)}
              onChange={(e) => {
                const next = new Set(selected);
                if (e.target.checked) next.add(opt);
                else next.delete(opt);
                onChange({ kind: 'enum', values: [...next] });
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }

  // datetime range
  return (
    <div className="flex flex-col gap-2">
      <label className="flex flex-col gap-0.5 text-xs text-white/65">{t("viewers.from")}<input
          type="datetime-local"
          className={inputCls}
          value={value.gte ?? ''}
          onChange={(e) => onChange({ ...value, gte: e.target.value || undefined })}
        />
      </label>
      <label className="flex flex-col gap-0.5 text-xs text-white/65">
        To
        <input
          type="datetime-local"
          className={inputCls}
          value={value.lte ?? ''}
          onChange={(e) => onChange({ ...value, lte: e.target.value || undefined })}
        />
      </label>
    </div>
  );
}
